import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faTrashAlt,
  faEdit,
  faEnvelope,
  faArrowUp,
  faFileExcel,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Addstudent from "../Addstudent/Addstudent";
import ViewStudent from "../ViewStudent/ViewStudent";
import { toast, ToastContainer } from "react-toastify";
import { utils, writeFile } from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import "../Student/Student.css";
import "bootstrap/dist/css/bootstrap.min.css";

const API_URL = "https://aadyaacademy.org/api";

function Student() {
  const [studentList, setStudentList] = useState([]);
  const [filteredStudent, setFilteredStudent] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [addingStudent, setAddingStudent] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState("");
  const [sections, setSections] = useState([]);
  const [classes, setClasses] = useState([]);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [sendingMessageToId, setSendingMessageToId] = useState(null);

  // Export full data
  const exportToExcel = () => {
    const dataToExport = filteredStudent.map((s) => ({
      "ID": s.student_id,
      "Name": s.student_name,
      "Aadhar": s.student_aadhar,
      "Class": s.class,
      "Section": s.section,
      "Gender": s.gender,
      "DOB": s.dob,
      "Blood Group": s.student_blood_group,
      "Academic Year": s.academic_year_joined,
      "Caste": s.caste,
      "Nationality": s.nationality,
      "Mother Tongue": s.mother_tongue,
      "Address": s.address,
      "Home Town": s.home_town,
      "Father Name": s.father_name,
      "Father Phone": s.father_phone,
      "Father Email": s.father_email,
      "Father Occupation": s.father_occupation,
      "Father Aadhar": s.father_aadhar,
      "Mother Name": s.mother_name,
      "Mother Phone": s.mother_phone,
      "Mother Email": s.mother_email,
      "Mother Occupation": s.mother_occupation,
      "Mother Aadhar": s.mother_aadhar,
      "Guardian Name": s.guardian_name,
      "Guardian Phone": s.guardian_phone,
      "Guardian Email": s.guardian_email,
      "Guardian Aadhar": s.guardian_aadhar,
    }));
    const worksheet = utils.json_to_sheet(dataToExport);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Students");
    writeFile(workbook, "Student_List.xlsx");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsResponse, sectionsResponse, classesResponse] = await Promise.all([
        axios.get(`${API_URL}/students`),
        axios.get(`${API_URL}/sections`),
        axios.get(`${API_URL}/classes`),
      ]);
      setStudentList(studentsResponse.data || []);
      setSections(sectionsResponse.data.sections.map((section) => section.name));
      setClasses(classesResponse.data.classes.map((cls) => cls.name));
    } catch (error) {
      toast.error("Error fetching data. Please try again.");
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...studentList];
    if (searchTerm) {
      filtered = filtered.filter((student) => {
        const searchValue = searchTerm.toLowerCase();
        return (
          student.student_id?.toString().includes(searchValue) ||
          student.student_name?.toLowerCase().includes(searchValue) ||
          student.class?.toLowerCase().includes(searchValue) ||
          student.section?.toLowerCase().includes(searchValue) ||
          student.gender?.toLowerCase().includes(searchValue) ||
          (student.dob && new Date(student.dob).toISOString().split("T")[0].includes(searchValue))
        );
      });
    }
    if (classFilter) filtered = filtered.filter((s) => s.class === classFilter);
    if (sectionFilter) filtered = filtered.filter((s) => s.section === sectionFilter);
    filtered.sort((a, b) => (a.student_name || "").localeCompare(b.student_name || ""));
    setFilteredStudent(filtered);
  }, [searchTerm, classFilter, sectionFilter, studentList]);

  const handleCancel = () => {
    setAddingStudent(false);
    setEditingStudent(null);
    setViewingStudent(null);
    setDeletingStudent(null);
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setAddingStudent(true);
  };

  const handleView = (student) => setViewingStudent(student);

  const handlePromote = async (student) => {
    let currentClass = student.class?.trim();
    if (!/^\d+$/.test(currentClass)) {
      toast.warn(`Cannot promote ${student.student_name} — class "${currentClass}" is not numeric.`);
      return;
    }
    const nextClass = (parseInt(currentClass, 10) + 1).toString();
    if (window.confirm(`Promote ${student.student_name} from ${currentClass} to ${nextClass}?`)) {
      try {
        await axios.post(`${API_URL}/students/${student.student_id}/promote`, { next_class: nextClass });
        toast.success(`Successfully promoted ${student.student_name}!`);
        fetchData();
      } catch (error) {
        toast.error("Failed to promote student.");
      }
    }
  };

  const formatDateToDisplay = (date) => {
    if (!date) return "N/A";
    const [year, month, day] = date.split("-");
    return `${day}/${month}/${year}`;
  };

  const getCurrentDate = () => new Date().toISOString().split("T")[0];

  const sendMessageToStudent = (student) => {
    setSendingMessageToId(student.student_id);
    toast.info(`Sending message to ${student.student_name}...`);
    setTimeout(() => {
      toast.info("Feature in development");
      setSendingMessageToId(null);
    }, 1000);
  };

  if (addingStudent) return <Addstudent handleCancel={handleCancel} fetchData={fetchData} editingStudent={editingStudent} />;
  if (viewingStudent) return <ViewStudent student={viewingStudent} onClose={handleCancel} />;

  return (
    <div className="student">
      <div className="student-table">
        <div className="table-header">
          <div className="table-header-btm">
            <input type="text" className="search-input" placeholder="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <input type="date" className="date-picker" onChange={(e) => setSelectedDate(formatDateToDisplay(e.target.value))} max={getCurrentDate()} />
            <select className="class-filter" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
              <option value="">Class</option>
              {classes.map((cls) => <option key={cls} value={cls}>{cls}</option>)}
            </select>
            <select className="section-filter" value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
              <option value="">Section</option>
              {sections.map((section) => <option key={section} value={section}>{section}</option>)}
            </select>
            
            <button onClick={exportToExcel} className="btn btn-success" style={{ marginLeft: "10px" }}>
              <FontAwesomeIcon icon={faFileExcel} /> Excel
            </button>

            <button onClick={() => setAddingStudent(true)} className="add-student-btn">
              <FontAwesomeIcon icon={faPlus} /> Add New
            </button>
          </div>
        </div>

        <div className="table-content">
          <table>
            <thead>
              <tr>
                <th>ID</th><th>Name</th><th>Class</th><th>Section</th><th>Gender</th><th>DOB</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : filteredStudent.map((student) => (
                <tr key={student.student_id} onClick={(e) => !e.target.closest(".action-btn") && handleView(student)} style={{ cursor: "pointer" }}>
                  <td>{student.student_id}</td>
                  <td>{student.student_name}</td>
                  <td>{student.class}</td>
                  <td>{student.section}</td>
                  <td>{student.gender}</td>
                  <td>{formatDateToDisplay(student.dob)}</td>
                  <td className="actions">
                    <button onClick={() => handleView(student)} className="action-btn"><FontAwesomeIcon icon={faEye} /></button>
                    <button onClick={() => handleEdit(student)} className="action-btn"><FontAwesomeIcon icon={faEdit} /></button>
                    <button onClick={() => setDeletingStudent(student)} className="action-btn"><FontAwesomeIcon icon={faTrashAlt} /></button>
                    <button onClick={() => sendMessageToStudent(student)} disabled={sendingMessageToId === student.student_id} className="action-btn"><FontAwesomeIcon icon={faEnvelope} /></button>
                    <button onClick={() => handlePromote(student)} className="action-btn" style={{ color: "#28a745" }}><FontAwesomeIcon icon={faArrowUp} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default Student;